import { useApolloClient } from "@apollo/client";
import {
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback } from "react";

import { AiInterviewerFragment } from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import IntroMediaUpload from "./IntroMediaUpload";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

const AiInterviewerSettingsContentSettings: React.FC<{
  aiInterviewer: AiInterviewerFragment;
  readOnly: boolean;
}> = ({ aiInterviewer, readOnly }) => {
  const { register, getValues } = useAiInterviewerFormContext();
  const showVerbatimMode = useFeatureFlagForCurrentOrImpersonatedUser(
    "ai_interviewer:verbatim_mode"
  );
  const apolloClient = useApolloClient();
  const refetchAiInterviewer = useCallback(() => {
    apolloClient.refetchQueries({ include: ["AiInterviewer"] });
  }, [apolloClient]);
  return (
    <VStack spacing={6} align="stretch" w="100%">
      <VStack align="stretch" spacing={1}>
        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
          Org or position information
        </Text>
        <Text fontSize="sm" color="gray.600">
          Provide info about the company or position to train the AI interviewer
          on how to best answer candidate questions.
        </Text>
      </VStack>
      <FormControl id="positionInfo">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Org or position info
        </FormLabel>
        <Textarea
          {...register("positionInfo")}
          placeholder="Optional: Will be used to answer candidate questions"
          fontSize="sm"
          borderRadius="md"
          isDisabled={readOnly}
          rows={3}
        />
      </FormControl>

      <VStack align="stretch" spacing={1}>
        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
          Video intro
        </Text>
        <Text fontSize="sm" color="gray.600">
          Make a strong first impression with a video message explaining the
          company, position, and/or team.
        </Text>
      </VStack>
      <IntroMediaUpload
        aiInterviewerId={aiInterviewer.id}
        currentMediaUrl={aiInterviewer.currentVersion?.introMedia?.url}
        currentMediaFormat={aiInterviewer.currentVersion?.introMedia?.format}
        currentMediaDurationSeconds={
          aiInterviewer.currentVersion?.introMedia?.durationSeconds
        }
        currentMediaFileName={
          aiInterviewer.currentVersion?.introMedia?.fileName
        }
        hasIntroMedia={!!aiInterviewer.currentVersion?.introMedia?.url}
        readOnly={readOnly}
        onUploadComplete={() => {
          refetchAiInterviewer();
        }}
        onRemoveComplete={() => {
          refetchAiInterviewer();
        }}
      />

      <VStack align="stretch" spacing={1}>
        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
          Opening
        </Text>
        <Text fontSize="sm" color="gray.600">
          Customize what the AI interviewer will say at the start of every call.
        </Text>
      </VStack>
      <FormControl id="introduction">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Script for AI interviewer
        </FormLabel>
        <Textarea
          {...register("introduction")}
          placeholder="Optional: What the AI recruiter should say at the start of the interview. It might include an overview of the company and position."
          fontSize="sm"
          borderRadius="md"
          isDisabled={readOnly}
          rows={3}
        />
      </FormControl>

      <VStack align="stretch" spacing={1}>
        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
          Closing
        </Text>
        <Text fontSize="sm" color="gray.600">
          Customize what the AI interviewer will say at the end of every call.
        </Text>
      </VStack>
      <FormControl id="closing">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Script for AI interviewer
        </FormLabel>
        <Textarea
          {...register("closing")}
          placeholder="Optional: What the AI recruiter should say at the end of the interview? It might include next steps."
          fontSize="sm"
          borderRadius="md"
          isDisabled={readOnly}
          rows={3}
        />
      </FormControl>

      <Divider />
      <Flex flexDirection="column">
        <Text fontSize="md" fontWeight="semibold">
          Manage job description
        </Text>
        <Text fontSize="sm" color="gray.600" mb={2}>
          We&apos;ll use this job description to answer candidate questions.
        </Text>
      </Flex>
      <FormControl id="jobDescription">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Job description
        </FormLabel>
        <Textarea
          {...register("jobDescription")}
          placeholder="Paste or write your job description here."
          fontSize="sm"
          borderRadius="md"
          isDisabled={readOnly}
          rows={3}
        />
      </FormControl>

      {showVerbatimMode && (
        <>
          <Divider />
          <FormControl>
            <Text fontSize="md" fontWeight="semibold">
              Phrasing for questions, intro, and closing
            </Text>

            <RadioGroup defaultValue={getValues("verbatim") || "false"}>
              <Flex direction="column" gap={2} mt={2}>
                <Radio {...register("verbatim")} value="false" size="sm">
                  <Text fontSize="sm">
                    Use adaptive phrasing to improve flow and responsiveness.
                  </Text>
                </Radio>
                <Radio {...register("verbatim")} value="true" size="sm">
                  <Text fontSize="sm">
                    Use provided phrasing to improve consistency.
                  </Text>
                </Radio>
              </Flex>
            </RadioGroup>
          </FormControl>
        </>
      )}
    </VStack>
  );
};

export default AiInterviewerSettingsContentSettings;
